import React from "react";
import { Link } from "react-router-dom";
import {
  History as HistoryIcon, FileText, StickyNote, CalendarClock, Info,
  ArrowUpRight, Phone, Mail, MessageCircle, Video, Users as UsersIcon, CheckCircle2, CalendarPlus,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Card } from "@/components/placements/ContextTabs";
import { fmtDateFr, relAgo, daysUntil } from "@/components/placements/placementsShared";
import { useLang, translateFr as tfr } from "@/i18n";

const INTER_ICONS = { call: Phone, email: Mail, whatsapp: MessageCircle, video: Video, meeting: UsersIcon, note: StickyNote };
const INTER_FR = { call: "Appel", email: "Email", whatsapp: "WhatsApp", video: "Visio", meeting: "Rendez-vous", note: "Note" };

const ACTIVITY_LABELS = {
  DISCUSSION_MANDATE_CONFIRMED: tfr("Mandat confirmé"),
  DISCUSSION_MILESTONE_COMPLETED: "Jalon franchi",
  DISCUSSION_STAGE_CHANGED: tfr("Étape modifiée"),
  DISCUSSION_MILESTONE_RESET: tfr("Jalon réinitialisé"),
  DISCUSSION_EXPECTATION_ADDED: tfr("Attente enregistrée"),
  DISCUSSION_ATTENTION_ADDED: tfr("Point d'attention enregistré"),
  DISCUSSION_ITEM_RESOLVED: tfr("Point traité"),
  DISCUSSION_TERMS_UPDATED: tfr("Conditions mises à jour"),
  DISCUSSION_INTERLOCUTOR_ADDED: tfr("Interlocuteur ajouté"),
  DISCUSSION_ACTION_COMPLETED: tfr("Action réalisée"),
  DISCUSSION_BLOCKED: tfr("Discussion marquée bloquée"),
  DISCUSSION_UNBLOCKED: tfr("Blocage levé"),
  FOLLOW_UP_CREATED: tfr("Relance planifiée"),
  FOLLOW_UP_COMPLETED: tfr("Relance terminée"),
  EVENT_CREATED: tfr("Événement ajouté au calendrier"),
  PLAYER_STAGE_CHANGED: tfr("Changement de statut"),
  INTERACTION_LOGGED: "Interaction",
  DOCUMENT_UPLOADED: tfr("Document ajouté"),
  PLAYER_CREATED: tfr("Athlète créé"),
};

function dayKey(iso) { return (iso || "").slice(0, 10); }
function dayLabel(iso, tf) {
  const d = daysUntil(iso);
  if (d === 0) return tf("Aujourd'hui");
  if (d === -1) return tf("Hier");
  return fmtDateFr(iso);
}

export function HistoryTab({ detail }) {
  const { tf } = useLang();
  const entries = [];
  (detail.interactions || []).forEach((i) => entries.push({
    at: i.occurred_at, kind: "interaction", type: i.type,
    label: tf(INTER_FR[i.type] || i.type), text: i.note,
  }));
  (detail.history || []).forEach((a) => {
    if (a.action_type === "INTERACTION_LOGGED") return;
    if (a.action_type === "DISCUSSION_STAGE_CHANGED") {
      const md = a.metadata || {};
      entries.push({
        at: a.created_at, kind: "activity",
        label: tf(ACTIVITY_LABELS.DISCUSSION_STAGE_CHANGED),
        text: `${tf(md.from || "")} → ${tf(md.to || "")}${md.note ? ` — ${md.note}` : ""}`,
      });
      return;
    }
    entries.push({
      at: a.created_at, kind: "activity",
      label: tf(ACTIVITY_LABELS[a.action_type] || a.action_type),
      text: a.metadata?.milestone || a.metadata?.content || a.metadata?.note || a.metadata?.name || a.metadata?.title
        || (a.metadata?.to ? `${a.metadata.from ? a.metadata.from + " → " : ""}${a.metadata.to}` : ""),
    });
  });
  entries.sort((x, y) => (y.at || "").localeCompare(x.at || ""));
  const groups = [];
  entries.slice(0, 60).forEach((e) => {
    const k = dayKey(e.at);
    const g = groups[groups.length - 1];
    if (g && g.key === k) g.items.push(e);
    else groups.push({ key: k, label: dayLabel(e.at, tf), items: [e] });
  });
  return (
    <Card title={tf("Historique")} Icon={HistoryIcon} testid="tab-history">
      {groups.length === 0 ? (
        <p className="text-sm text-muted-foreground">{tf("Aucune activité enregistrée.")}</p>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">{g.label}</div>
              <ul className="space-y-2.5">
                {g.items.map((e, i) => {
                  const Icon = e.kind === "interaction" ? (INTER_ICONS[e.type] || StickyNote) : CheckCircle2;
                  return (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <span className="text-[10px] text-muted-foreground tabular-nums w-10 shrink-0 mt-0.5">
                        {(e.at || "").slice(11, 16) || ""}
                      </span>
                      <Icon className={cn("h-3.5 w-3.5 mt-0.5 shrink-0",
                        e.kind === "interaction" ? "text-info" : "text-muted-foreground")} />
                      <div className="min-w-0 break-words">
                        <span className="font-medium">{e.label}</span>
                        {e.text && <span className="text-muted-foreground"> · {e.text}</span>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function DocumentsTab({ detail }) {
  const { tf } = useLang();
  const docs = detail.documents || [];
  const pid = detail.athlete?.id;
  return (
    <Card title={tf("Documents de l'athlète")} Icon={FileText} testid="tab-documents">
      {docs.length === 0 ? (
        <p className="text-sm text-muted-foreground">{tf("Aucun document.")}</p>
      ) : (
        <ul className="divide-y divide-border dark:divide-white/[0.06]">
          {docs.slice(0, 12).map((d) => (
            <li key={d.id} className="py-2 flex items-center justify-between gap-3 text-sm">
              <span className="truncate">{d.label || d.doc_type}</span>
              <span className="text-xs text-muted-foreground shrink-0">{fmtDateFr(d.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
      <Button asChild size="sm" variant="outline" className="mt-3 h-8 gap-1.5 border-border dark:border-white/10">
        <Link to={`/players/${pid}`} data-testid="manage-docs-link">
          {tf("Gérer les documents")} <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </Button>
    </Card>
  );
}

export function NotesTab({ detail, onAddInteraction }) {
  const { tf } = useLang();
  const notes = detail.notes || [];
  return (
    <Card title={tf("Notes")} Icon={StickyNote} testid="tab-notes"
      action={detail.status === "OPEN" && (
        <button onClick={onAddInteraction} className="text-[11px] text-info hover:underline" data-testid="notes-add-btn">
          + {tf("Ajouter une note")}
        </button>)}>
      {notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">{tf("Aucune note pour cette discussion.")}</p>
      ) : (
        <ul className="space-y-3">
          {notes.slice(0, 20).map((n) => (
            <li key={n.id} className="text-sm">
              <div className="text-[11px] text-muted-foreground mb-0.5">{fmtDateFr(n.occurred_at)}</div>
              <div className="break-words leading-relaxed">{n.note}</div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function ActionsTab({ detail, onRefresh, onPlan }) {
  const { tf } = useLang();
  const isOpen = detail.status === "OPEN";
  const actions = detail.open_actions || [];
  const events = detail.upcoming_events || [];
  const complete = async (rid) => {
    try {
      await api.post(`/discussions/${detail.id}/actions/${rid}/complete`);
      toast.success(tf("Action réalisée")); onRefresh?.();
    } catch { toast.error(tf("Erreur")); }
  };
  const toCalendar = async (rid) => {
    try {
      await api.post(`/discussions/${detail.id}/actions/${rid}/calendar`);
      toast.success(tf("Ajouté au calendrier")); onRefresh?.();
    } catch { toast.error(tf("Erreur")); }
  };
  return (
    <Card title={tf("Prochaines actions")} Icon={CalendarClock} testid="tab-actions"
      action={isOpen && (
        <button onClick={onPlan} className="text-[11px] text-info hover:underline" data-testid="actions-plan-btn">
          + {tf("Planifier une action")}
        </button>)}>
      {actions.length === 0 && events.length === 0 ? (
        <p className="text-sm text-muted-foreground">{tf("Aucune action en attente.")}</p>
      ) : (
        <ul className="space-y-2.5">
          {actions.map((a) => {
            const overdue = a.days != null && a.days < 0;
            return (
              <li key={a.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`action-${a.id}`}>
                <div className="min-w-0">
                  <div className="font-medium break-words">{a.note || tf("Suivi")}</div>
                  <div className={cn("text-xs", overdue ? "text-athlynx" : "text-muted-foreground")}>
                    {fmtDateFr(a.due_at)}{overdue && ` · ${tf("En retard de")} ${Math.abs(a.days)} j`}
                  </div>
                </div>
                {isOpen && (
                  <span className="flex gap-1 shrink-0">
                    <button onClick={() => toCalendar(a.id)} title={tf("Ajouter au calendrier")}
                      disabled={!!a.calendar_event_id}
                      className={cn("p-1.5 rounded-md", a.calendar_event_id ? "text-muted-foreground/30" : "text-info hover:bg-info/10")}
                      data-testid={`action-cal-${a.id}`}>
                      <CalendarPlus className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => complete(a.id)} title={tf("Marquer comme réalisée")}
                      className="p-1.5 rounded-md text-success hover:bg-success/10" data-testid={`action-done-${a.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    </button>
                  </span>
                )}
              </li>
            );
          })}
          {events.map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`event-${e.id}`}>
              <div className="min-w-0">
                <div className="font-medium break-words">{e.title}</div>
                <div className="text-xs text-muted-foreground">{tf("Agenda")} · {fmtDateFr(e.starts_at)}</div>
              </div>
              <Link to="/calendar" className="text-info shrink-0 p-1.5 rounded-md hover:bg-info/10" aria-label={tf("Ouvrir le calendrier")}>
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function ContexteTab({ detail, onRefresh }) {
  const { tf } = useLang();
  const a = detail.athlete || {};
  const [editing, setEditing] = React.useState(false);
  const [src, setSrc] = React.useState("");
  const save = async () => {
    try {
      await api.patch(`/discussions/${detail.id}`, { source: src });
      setEditing(false); onRefresh?.();
    } catch { toast.error(tf("Erreur")); }
  };
  return (
    <Card title={tf("Contexte")} Icon={Info} testid="tab-contexte">
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
          <span className="text-xs text-muted-foreground shrink-0">{tf("Origine du contact")}</span>
          {editing ? (
            <span className="flex gap-2 flex-1 justify-end min-w-0">
              <input value={src} onChange={(e) => setSrc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()}
                className="flex-1 min-w-0 max-w-[260px] h-8 rounded-lg border border-border dark:border-white/10 bg-background/60 px-2 text-xs outline-none focus:border-info/50"
                data-testid="context-source-input" />
              <Button size="sm" onClick={save} className="h-8" data-testid="context-source-save">{tf("OK")}</Button>
            </span>
          ) : (
            <button onClick={() => { if (detail.status === "OPEN") { setSrc(detail.source || ""); setEditing(true); } }}
              className={cn("font-medium text-right min-w-0 break-words",
                !detail.source && "text-muted-foreground/50 font-normal",
                detail.status === "OPEN" && "hover:text-info")}
              data-testid="context-source">
              {detail.source || tf("Non renseigné")}
            </button>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
          <span className="text-xs text-muted-foreground">{tf("Début de la discussion")}</span>
          <span className="font-medium">{fmtDateFr(detail.started_at)}</span>
        </div>
        <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
          <span className="text-xs text-muted-foreground">{tf("Statut Pipeline")}</span>
          <span className="font-medium">{a.status || "—"}</span>
        </div>
        <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
          <span className="text-xs text-muted-foreground">{tf("Dernier échange")}</span>
          <span className="font-medium">
            {a.last_interaction_at ? `${relAgo(a.last_interaction_at)} · ${fmtDateFr(a.last_interaction_at)}` : tf("Aucun")}
          </span>
        </div>
      </div>
      <Button asChild size="sm" variant="outline" className="mt-3 h-8 gap-1.5 border-border dark:border-white/10">
        <Link to={`/players/${a.id}`} data-testid="context-open-athlete">
          {tf("Ouvrir la fiche athlète")} <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </Button>
    </Card>
  );
}
